<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AppCard from '@/components/ui/AppCard.vue'
import DateField from '@/components/ui/DateField.vue'
import Button from 'primevue/button'
import Message from 'primevue/message'
import Skeleton from 'primevue/skeleton'
import Tag from 'primevue/tag'
import Select from 'primevue/select'
import InputText from 'primevue/inputtext'
import ConfirmDialog from '@/components/ui/Dialog.vue'
import AdvancesCard from '@/components/ui/AdvancesCard.vue'
import { useVolverAlListado } from '@/composables/useVolverAlListado'
import { useAuthStore } from '@/stores/auth'
import {
  quoteApi,
  NOTE_TYPES,
  type NoteType,
  type Quote,
  type QuoteAdvance,
} from '@/services/quotes'
import { formatCurrency, formatShortDate } from '@/utils/format'
import { statusLabel, statusSeverity } from '@/utils/status'
import QuoteDocument from '@/components/quotes/QuoteDocument.vue'

/**
 * Detalle de una cotización.
 *
 * Desde aquí se cambia el estado, se factura, se borra y se anotan seguimientos.
 * Todas las acciones destructivas piden confirmación: un toque de 44px en móvil
 * sobre "Facturar" o "Borrar" no tiene marcha atrás en la interfaz.
 */

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()

const canManage = computed(() => authStore.hasPermission('quotes.manage'))

const quote = ref<Quote | null>(null)
const loading = ref(false)
const loadError = ref<string | null>(null)
const actionError = ref<string | null>(null)
const acting = ref(false)

const deleteDialog = ref(false)
const invoiceDialog = ref(false)

const invoiceNumber = ref('')
const noteText = ref('')
const noteType = ref<NoteType>('general')
const noteDate = ref('')

const noteTypeOptions = NOTE_TYPES.map((value) => ({ label: value, value }))

const id = computed(() => {
  const parsed = Number.parseInt(String(route.params.id ?? ''), 10)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null
})

/**
 * Abre el diálogo de impresión del navegador.
 *
 * De ahí sale el PDF: en la ventana que se abre está la opción de guardar en PDF, y el
 * fichero sale con las fuentes y los colores de la aplicación, sin maquetar nada en el
 * servidor.
 *
 * El documento que se imprime no es esta pantalla sino el `<QuoteDocument>` de al lado,
 * que la hoja de estilo enseña en `@media print`. Lo único que hace falta es abrir el
 * diálogo.
 */
function imprimir(): void {
  window.print()
}

async function load(): Promise<void> {
  actionError.value = null

  if (id.value === null) {
    loadError.value = 'Esa cotización no existe.'
    return
  }

  loading.value = true

  try {
    const { data } = await quoteApi.get(id.value)
    quote.value = data.data
    // El historial de anticipos viene en el recurso de la ficha. En las listas llega
    // vacío a propósito: veinte cotizaciones con su historial de pagos serían veinte
    // listados que nadie lee.
    advances.value = data.data.advances ?? []
    // El detalle abre el diálogo de estado con el estado actual, para que
    // "cambiar estado" no empieza proposing donde ya está.
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    quote.value = null
    loadError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo cargar la cotización.'
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(id, load)

async function markInvoiced(): Promise<void> {
  if (quote.value === null) return

  acting.value = true
  try {
    const { data } = await quoteApi.updateStatus(quote.value.id, {
      status: quote.value.status === 'borrador' ? 'pendiente' : quote.value.status,
      is_invoiced: true,
      invoice_number: invoiceNumber.value.trim(),
    })
    quote.value = data.data
    invoiceDialog.value = false
    invoiceNumber.value = ''
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    actionError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo marcar como facturada.'
  } finally {
    acting.value = false
  }
}

/**
 * Volver al listado con los filtros con los que se llegó.
 *
 * Sin esto, «Volver» empuja una entrada nueva al listado sin la consulta y la lista vuelve
 * a mostrarlos todos: se aplican un filtro, se abre una cotización, se vuelve, y se
 * Compra todo el trabajo.
 */
const { destino: destinoListado, volver: volverAlListado } = useVolverAlListado('quotes.index')

async function confirmDelete(): Promise<void> {
  if (quote.value === null) return

  acting.value = true
  try {
    await quoteApi.remove(quote.value.id)
    await router.push(destinoListado.value)
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    actionError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo borrar la cotización.'
  } finally {
    acting.value = false
  }
}

async function addNote(): Promise<void> {
  if (quote.value === null || noteText.value.trim() === '') return

  acting.value = true
  try {
    await quoteApi.addNote(quote.value.id, {
      note: noteText.value.trim(),
      type: noteType.value,
      next_follow_up_date: noteDate.value === '' ? null : noteDate.value,
    })

    noteText.value = ''
    noteDate.value = ''

    // Se recarga en vez de parchear a mano el contador: la nota puede haber
    // movido la fecha de seguimiento, y ese dato solo lo devuelve el servidor.
    await load()
  } catch (cause: unknown) {
    const maybeAxios = cause as { response?: { data?: { error?: { message?: string } } } }
    actionError.value = maybeAxios.response?.data?.error?.message ?? 'No se pudo guardar la nota.'
  } finally {
    acting.value = false
  }
}
// ─── Anticipos ──────────────────────────────────────────────────────────────
//
// Van en su propia tarjeta y no dentro del formulario del cotizador porque un
// anticipo no es contenido de la cotización: es dinero que ya entró, y además se
// puede repetir. Meterlo en el cotizador mezclaría dos cosas que se escriben con
// reglas distintas —una se autoguarda y la otra no—.

const advances = ref<QuoteAdvance[]>([])

/** Cuánto falta por cobrar. Sale del servidor y no se recalcula aquí. */
const balance = computed(() => quote.value?.balance ?? '0.00')

/** El total de lo pagado por adelantado. */
const advancesTotal = computed(() => quote.value?.advances_total ?? '0.00')

/**
 * Recoge lo que cambia en la tarjeta de anticipos.
 *
 * La tarjeta va en su propio componente porque la tienen que ver dos pantallas —la
 * ficha y el editor— y porque su lógica también. Pegada en las dos, añadir un campo
 * al anticipo sería cambiarlo en dos sitios y basta con que uno se quede atrás para
 * que las dos digan cosas distintas.
 *
 * Aquí solo se copia lo que la tarjeta trae. El saldo y el total los recalcula el
 * servidor y se copian tal cual: volver a sumarlos aquí sería tener dos reglas para
 * la misma cuenta y acabar discrepando en algún céntimo.
 */
function cambiaAnticipos(totals: { advances_total: string; balance: string; advances: QuoteAdvance[] }): void {
  if (quote.value === null) return

  advances.value = totals.advances
  quote.value.advances_total = totals.advances_total
  quote.value.balance = totals.balance
  quote.value.advances_count = totals.advances.length
  quote.value.has_advances = totals.advances.length > 0
}
</script>

<template>
  <div class="animate-fade-in">
    <!--
      Todo lo que hay dentro es pantalla: cabecera con sus botones, datos, artículos y
      el panel de seguimiento con sus notas. Al imprimir, esto desaparece entero.

      Va en un `div` propio y no con `.no-print` repartido por cada bloque, porque lo
      que se imprime es el documento de al lado y basta con esconder **todo** lo demás.
      Si mañana la ficha gana un bloque nuevo, aquí se mete solo y no hay que acordarse
      de esconderlo.
    -->
    <div class="no-print space-y-6">
      <header class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="min-w-0">
          <p class="text-sm text-surface-500 dark:text-surface-400">
            <button
              type="button"
              class="hover:underline"
              @click="volverAlListado"
            >
              Cotizaciones
            </button>
            <span aria-hidden="true"> / </span>
            <span class="font-mono">{{ quote?.quote_number ?? route.params.id }}</span>
          </p>
          <h1 class="mt-1 truncate text-2xl font-semibold text-surface-900 dark:text-surface-100">
            {{ quote?.customer_name ?? 'Detalle de la cotización' }}
          </h1>
        </div>
        <div class="flex flex-wrap gap-2">
          <Button
            label="Volver"
            icon="pi pi-arrow-left"
            severity="secondary"
            @click="volverAlListado"
          />
          <!--
            Editar. Va con `quotes.manage` como las otras dos acciones, porque cambia
            el contenido de un documento comercial.

            Faltaba, y su falta era confusa: la ruta `/quotes/:id/edit` existía y
            apuntaba a esta misma ficha, que no edita nada. Abrirla enseñaba la
            cotización en lectura, que es la peor forma de no tener edición: parece que
            la hay.
          -->
          <Button
            v-if="canManage && quote"
            label="Editar"
            icon="pi pi-pencil"
            severity="secondary"
            outlined
            data-testid="quote-edit"
            @click="router.push({ name: 'quotes.edit', params: { id: String(quote.id) } })"
          />
          <!--
            Imprimir. No pide permiso: mirar una cotización y descargarla no cambia
            nada, y el botón se pone al lado de "Facturar" y "Borrar" para que se vea que
            es de lo mismo —solo que este no tiene por qué confirmar—.
          -->
          <Button
            label="Imprimir"
            icon="pi pi-print"
            severity="secondary"
            outlined
            data-testid="quote-print"
            @click="imprimir"
          />
          <Button
            v-if="canManage && quote && !quote.is_invoiced"
            label="Facturar"
            icon="pi pi-receipt"
            severity="success"
            @click="invoiceDialog = true"
          />
          <Button
            v-if="canManage && quote"
            label="Borrar"
            icon="pi pi-trash"
            severity="danger"
            outlined
            @click="deleteDialog = true"
          />
        </div>
      </header>

      <Message
        v-if="loadError"
        severity="error"
        :closable="false"
        variant="simple"
        role="alert"
      >
        {{ loadError }}
      </Message>

      <Message
        v-if="actionError"
        severity="error"
        :closable="false"
        variant="simple"
        role="alert"
      >
        {{ actionError }}
      </Message>

      <AppCard v-if="loading">
        <div class="space-y-4">
          <Skeleton
            v-for="index in 5"
            :key="index"
            height="1.25rem"
          />
        </div>
      </AppCard>

      <template v-else-if="quote">
        <!-- Cabecera -->
        <AppCard>
          <template #header>
            <div class="flex flex-wrap items-center gap-2">
              <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
                Cotización
              </h2>
              <Tag
                :value="statusLabel(quote.status)"
                :severity="statusSeverity(quote.status)"
              />
              <Tag
                v-if="quote.is_invoiced"
                :value="quote.invoice_number ?? 'Facturada'"
                severity="success"
              />
            </div>
          </template>

          <dl class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt class="text-sm text-surface-500 dark:text-surface-400">
                Cliente
              </dt>
              <dd class="text-surface-900 dark:text-surface-100">
                {{ quote.customer_name }}
              </dd>
            </div>
            <div>
              <dt class="text-sm text-surface-500 dark:text-surface-400">
                Documento
              </dt>
              <dd class="font-mono text-surface-900 dark:text-surface-100">
                {{ quote.document ?? '—' }}
              </dd>
            </div>
            <div>
              <dt class="text-sm text-surface-500 dark:text-surface-400">
                Fecha
              </dt>
              <dd class="text-surface-900 dark:text-surface-100">
                {{ formatShortDate(quote.quote_date) }}
              </dd>
            </div>
            <div>
              <dt class="text-sm text-surface-500 dark:text-surface-400">
                Asesor
              </dt>
              <dd class="text-surface-900 dark:text-surface-100">
                {{ quote.user_name ?? '—' }}
              </dd>
            </div>
            <!--
              Va junto al asesor porque es la otra mitad de a dónde va la cotización:
              quién la lleva y por qué línea entra. Faltaba en la ficha —sí estaba en
              el listado, el PDF, el Excel y el panel— así que en la pantalla donde se
              lee la cotización entera no se veía, y era justo donde más hace falta:
              para saber a quién hay que escribirle.

              Se enseña aunque venga vacía, con el guion largo, como el resto: que el
              campo desaparezca esconde si la cotización está sin clasificar.
            -->
            <div>
              <dt class="text-sm text-surface-500 dark:text-surface-400">
                Línea de atención
              </dt>
              <dd
                data-testid="quote-attention-line"
                class="text-surface-900 dark:text-surface-100"
              >
                {{ quote.attention_line ?? '—' }}
              </dd>
            </div>
            <div>
              <dt class="text-sm text-surface-500 dark:text-surface-400">
                Tipo de facturación
              </dt>
              <dd class="text-surface-900 dark:text-surface-100">
                {{ quote.price_type === 'wholesale' ? 'Venta al mayoreo' : 'Venta al detal' }}
              </dd>
            </div>
            <div>
              <dt class="text-sm text-surface-500 dark:text-surface-400">
                Seguimiento
              </dt>
              <dd class="text-surface-900 dark:text-surface-100">
                {{ formatShortDate(quote.next_follow_up_date) }}
              </dd>
            </div>
            <!--
            El contacto se enseña aunque venga vacío, con el guion largo. Es lo que se
            manda al cliente y la primera pregunta que le hacen; que el campo no exista
            cuando no se rellenó esconde si la cotización lleva forma de contacto.
          -->
            <div>
              <dt class="text-sm text-surface-500 dark:text-surface-400">
                Correo
              </dt>
              <dd class="text-surface-900 dark:text-surface-100">
                {{ quote.customer_email ?? '—' }}
              </dd>
            </div>
            <div>
              <dt class="text-sm text-surface-500 dark:text-surface-400">
                Teléfono
              </dt>
              <dd class="text-surface-900 dark:text-surface-100">
                {{ quote.customer_phone ?? '—' }}
              </dd>
            </div>

            <div class="lg:col-span-2">
              <dt class="text-sm text-surface-500 dark:text-surface-400">
                Total
              </dt>
              <dd
                data-testid="quote-total"
                class="text-2xl font-semibold text-surface-900 dark:text-surface-100"
              >
                {{ formatCurrency(quote.total_price) }}
              </dd>
              <dd class="mt-1 text-base font-medium text-surface-700 dark:text-surface-200">
                {{ quote.products_count }}
                {{ quote.products_count === 1 ? 'producto' : 'productos' }}
              </dd>
            </div>
          </dl>
        </AppCard>

        <!--
          Anticipos.

          Va después de la cabecera y antes de los artículos porque es dinero: es lo
          primero que se mira al abrir una cotización de la que ya se cobró parte.
          El total de arriba **no** baja con esto, y a propósito: es el número con el
          que se factura. Lo que baja es el saldo de esta tarjeta.
        -->
        <AdvancesCard
          v-if="quote !== null"
          :quote-id="quote.id"
          :advances="advances"
          :advances-total="advancesTotal"
          :balance="balance"
          @cambia="cambiaAnticipos"
        />

        <!-- Artículos -->
        <AppCard>
          <template #header>
            <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
              Artículos
            </h2>
          </template>

          <p
            v-if="quote.items.length === 0"
            class="py-6 text-center text-sm text-surface-500 dark:text-surface-400"
          >
            Esta cotización no tiene artículos.
          </p>

          <ul
            v-else
            class="divide-y divide-surface-200 dark:divide-surface-700"
            data-testid="quote-detail-items"
          >
            <li
              v-for="item in quote.items"
              :key="item.id"
              class="py-3"
            >
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="font-medium text-surface-900 dark:text-surface-100">
                    {{ item.name }}
                  </p>
                  <p class="text-sm font-medium text-surface-600 dark:text-surface-300">
                    <span
                      v-if="item.reference"
                      class="font-mono text-xs text-surface-400"
                    >{{ item.reference }} · </span>
                    <span class="text-base font-bold text-surface-900 dark:text-surface-100">
                      {{ item.quantity }}
                    </span>
                    × {{ formatCurrency(item.price) }}
                  </p>
                </div>
                <div class="flex shrink-0 items-center gap-2">
                  <Tag
                    :value="item.origin === 'set' ? 'Set' : item.origin === 'product' ? 'Producto' : 'A medida'"
                    :severity="item.origin === 'set' ? 'info' : 'secondary'"
                  />
                  <span class="font-medium text-surface-900 dark:text-surface-100">
                    {{ formatCurrency(item.line_total) }}
                  </span>
                </div>
              </div>

              <ul
                v-if="item.children.length > 0"
                class="mt-2 space-y-1 border-l-2 border-surface-200 pl-3 dark:border-surface-700"
              >
                <li
                  v-for="child in item.children"
                  :key="child.id"
                  class="grid grid-cols-[minmax(4.5rem,auto)_1fr_auto_auto] items-start gap-x-3 gap-y-1 text-sm text-surface-600 dark:text-surface-300"
                >
                  <!--
                    Código primero, en columna de ancho fijo, para que el nombre
                    empiece siempre en el mismo sitio y, si es largo, las líneas
                    siguientes queden alineadas bajo el nombre y no bajo el código.
                    La cantidad y el precio van en una fila propia al final, para
                    que no se escondan tras un nombre largo.
                  -->
                  <span class="font-mono text-xs text-surface-400">
                    {{ child.reference ?? '' }}
                  </span>
                  <p class="min-w-0 break-words">{{ child.name }}</p>
                  <span class="text-sm font-bold text-surface-700 dark:text-surface-200">
                    × {{ child.quantity }}
                  </span>
                  <span class="shrink-0">{{ formatCurrency(child.line_total) }}</span>
                </li>
              </ul>
            </li>
          </ul>
        </AppCard>

        <!-- Seguimiento -->
        <AppCard>
          <template #header>
            <h2 class="text-lg font-semibold text-surface-900 dark:text-surface-100">
              Seguimiento
            </h2>
          </template>

          <form
            v-if="canManage"
            class="mb-5 space-y-3"
            novalidate
            @submit.prevent="addNote"
          >
            <InputText
              v-model="noteText"
              placeholder="¿Qué se dijo en el último contacto?"
              class="w-full"
              aria-label="Texto de la nota"
            />
            <div class="flex flex-col gap-2 sm:flex-row">
              <Select
                v-model="noteType"
                :options="noteTypeOptions"
                option-label="label"
                option-value="value"
                aria-label="Tipo de nota"
                class="w-full sm:w-40"
              />
              <DateField
                v-model="noteDate"
                input-id="note-date"
                aria-label="Próximo contacto"
                class="sm:w-44"
              />
              <Button
                type="submit"
                label="Añadir nota"
                icon="pi pi-plus"
                :loading="acting"
                :disabled="noteText.trim() === ''"
              />
            </div>
          </form>

          <p
            v-if="quote.notes.length === 0"
            class="py-6 text-center text-sm text-surface-500 dark:text-surface-400"
          >
            Sin notas de seguimiento.
          </p>

          <ul
            v-else
            class="space-y-3"
            data-testid="quote-notes"
          >
            <li
              v-for="note in quote.notes"
              :key="note.id"
              class="rounded border border-surface-200 p-3 dark:border-surface-700"
            >
              <div class="flex flex-wrap items-center gap-2">
                <Tag
                  :value="note.type"
                  severity="secondary"
                />
                <span
                  v-if="note.user_name"
                  class="text-sm text-surface-600 dark:text-surface-300"
                >
                  {{ note.user_name }}
                </span>
                <span
                  v-if="note.created_at"
                  class="ml-auto text-xs text-surface-500"
                >
                  {{ formatShortDate(note.created_at) }}
                </span>
              </div>
              <p class="mt-2 text-surface-900 dark:text-surface-100">
                {{ note.note }}
              </p>
              <p
                v-if="note.next_follow_up_date"
                class="mt-1 text-xs text-surface-500 dark:text-surface-400"
              >
                Próximo contacto: {{ formatShortDate(note.next_follow_up_date) }}
              </p>
            </li>
          </ul>
        </AppCard>
      </template>

      <AppCard v-else-if="!loadError">
        <div class="py-12 text-center">
          <p class="text-surface-500 dark:text-surface-400">
            No hay nada que mostrar.
          </p>
        </div>
      </AppCard>

      <!-- Facturación -->
      <ConfirmDialog
        v-model:visible="invoiceDialog"
        title="Marcar como facturada"
        @confirm="markInvoiced"
        @cancel="invoiceDialog = false"
      >
        <div class="space-y-2">
          <label
            for="invoice-number"
            class="block text-sm font-medium text-surface-700 dark:text-surface-300"
          >
            Número de factura <span class="text-red-500">*</span>
          </label>
          <InputText
            id="invoice-number"
            v-model="invoiceNumber"
            placeholder="FV-2026-0001"
            class="w-full"
          />
          <p class="text-sm text-surface-500 dark:text-surface-400">
            Una cotización facturada sin número no se puede auditar, así que es obligatorio.
          </p>
        </div>
      </ConfirmDialog>

      <!-- Borrado -->
      <ConfirmDialog
        v-model:visible="deleteDialog"
        title="Borrar cotización"
        @confirm="confirmDelete"
        @cancel="deleteDialog = false"
      >
        <p class="text-surface-700 dark:text-surface-300">
          Se va a borrar la cotización de
          <strong>{{ quote?.customer_name }}</strong>, con sus artículos y sus notas de
          seguimiento. Esta acción no se puede deshacer.
        </p>
      </ConfirmDialog>
    </div>

    <!--
      El documento, montado siempre y escondido en pantalla: la hoja de estilo lo saca
      con `.print-only`, que está fuera del `@media` justamente para que en pantalla no
      se vea.

      Montarlo siempre y no en el momento de pulsar el botón es a propósito. Si se
      construyera entonces habría que esperar a `nextTick`, y los datos ya están en
      memoria: no hay nada que pedirle al servidor. Y una copia siempre presente no
      puede quedarse atrás si el documento cambia.
    -->
    <QuoteDocument
      v-if="quote"
      :quote="quote"
      class="print-only"
    />
  </div>
</template>
